/* Vacaciones: los días que le corresponden a cada uno por antigüedad.
 *
 * ── DE DÓNDE SALE LA REGLA ───────────────────────────────────────────────
 *
 * Pedido de Natalia Cuffone (Dto. de Personal, 08/09/2026, P-172). Sale del
 * **artículo 46 del CCT 40/89** — el convenio de camioneros —, que fija los
 * días corridos de vacaciones por antigüedad:
 *
 *   hasta 5 años ................ 14 días corridos
 *   más de 5 y hasta 10 ......... 21 días corridos
 *   más de 10 y hasta 20 ........ 28 días corridos
 *   más de 20 ................... 35 días corridos
 *
 * ── LA ANTIGÜEDAD SE MIDE AL 31/12 DEL AÑO, NO A HOY ─────────────────────
 *
 * La ley (LCT art. 150, que el CCT sigue) computa la antigüedad **al 31 de
 * diciembre del año al que corresponden las vacaciones**. Si se midiera a la
 * fecha de hoy, alguien que cumple los 5 años en noviembre quedaría un día en
 * 14 y al otro en 21 según cuándo se abra la pantalla — el mismo dato daría
 * dos números. El corte al 31/12 lo hace estable y es el que usa RRHH.
 *
 * ── EL PROPORCIONAL DEL PRIMER AÑO (LCT art. 151) ────────────────────────
 *
 * Natalia lo confirmó (08/09/2026): además de las cuatro escalas, quien
 * **ingresó dentro del año** y no llegó al mínimo de medio año trabajado cobra
 * un **día cada veinte trabajados**, no la escala entera. Un ingreso de
 * noviembre no corresponde 14 días: corresponde el proporcional de lo que
 * trabajó. Ver `diasPorAntiguedad` y `esProporcional`.
 *
 * El mínimo del art. 151 es «la mitad, como mínimo, de los días hábiles del
 * año»; se aproxima a **seis meses / 183 días corridos** —no llevamos un
 * calendario de días hábiles— y los veinte son días corridos trabajados. Es
 * una ayuda de cálculo que RRHH revisa y tilda OK, no una liquidación: el
 * número se muestra marcado como proporcional para que se vea de dónde sale.
 *
 * `diasPorAntiguedad` sigue devolviendo `null` cuando no puede decidir (sin
 * fecha de ingreso), y la pantalla lo muestra «—», no cero: «no sé» no es
 * «cero». Pero un proporcional de 0 (trabajó menos de 20 días) SÍ es cero —
 * ahí cero es la respuesta correcta, no una falta de dato.
 */

/* La fecha de ingreso computable, como Date, o null si no se puede leer.
 * `ingreso_computable` viene de la nómina como texto ISO (YYYY-MM-DD). */
export function fechaIngreso(valor) {
  const s = String(valor ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* El año de vacaciones por defecto: el año en curso (hora local del navegador,
 * que en el hub es la del que mira). Se pasa explícito en los tests. */
export function anioVacaciones(hoy = new Date()) {
  return hoy.getFullYear();
}

/* Los años cumplidos entre el ingreso y el 31/12 del año dado. Enteros: la
 * antigüedad para la escala se cuenta en años cumplidos, no fraccionados. */
export function antiguedadAlCierre(ingreso, anio) {
  const f = ingreso instanceof Date ? ingreso : fechaIngreso(ingreso);
  if (!f) return null;
  const cierre = new Date(`${anio}-12-31T00:00:00`);
  if (f > cierre) return null; // ingresó después del cierre: no corresponde aún
  let años = anio - f.getFullYear();
  // Si todavía no llegó al aniversario dentro de ese año, resta uno. Al 31/12
  // eso sólo pasa si ingresó un 31/12 — pero se calcula bien igual.
  const aniversario = new Date(`${anio}-${String(f.getMonth() + 1).padStart(2, "0")}-${String(f.getDate()).padStart(2, "0")}T00:00:00`);
  if (aniversario > cierre) años -= 1;
  return años;
}

/* Los días corridos efectivamente trabajados en el año: del ingreso (o del
 * 1/1 si ingresó antes) al 31/12, contando las dos puntas. `null` sin fecha o
 * si ingresó después del cierre. Es la base del proporcional del primer año. */
export function diasTrabajadosEnAnio(ingreso, anio = anioVacaciones()) {
  const f = ingreso instanceof Date ? ingreso : fechaIngreso(ingreso);
  if (!f) return null;
  const inicio = new Date(`${anio}-01-01T00:00:00`);
  const cierre = new Date(`${anio}-12-31T00:00:00`);
  if (f > cierre) return null;
  const desde = f > inicio ? f : inicio;
  return Math.round((cierre - desde) / (24 * 3600 * 1000)) + 1;
}

// Mínimo del art. 151 LCT para el período completo: la mitad del año, como
// mínimo (aproximado a medio año / 183 días corridos).
const MINIMO_ANIO_COMPLETO = 183;

/* ¿Le toca el PROPORCIONAL del primer año, en vez de la escala? Sólo a quien
 * ingresó DENTRO del año y no llegó al mínimo de medio año trabajado. */
export function esProporcional(ingreso, anio = anioVacaciones()) {
  const f = ingreso instanceof Date ? ingreso : fechaIngreso(ingreso);
  if (!f || f.getFullYear() !== anio) return false;
  const trab = diasTrabajadosEnAnio(f, anio);
  return trab !== null && trab < MINIMO_ANIO_COMPLETO;
}

/* Los días de vacaciones que corresponden. `null` si no se puede saber la
 * antigüedad (sin fecha de ingreso, o ingreso posterior al cierre) — nunca 0
 * por «no sé». Para el primer año incompleto, el proporcional del art. 151
 * (un día cada veinte trabajados); para el resto, la escala del art. 46. */
export function diasPorAntiguedad(ingreso, anio = anioVacaciones()) {
  const años = antiguedadAlCierre(ingreso, anio);
  if (años === null || años < 0) return null;
  if (esProporcional(ingreso, anio)) {
    return Math.floor(diasTrabajadosEnAnio(ingreso, anio) / 20);
  }
  if (años <= 5) return 14;
  if (años <= 10) return 21;
  if (años <= 20) return 28;
  return 35;
}

/* Los días corridos de un período tomado, contando las dos puntas. «del 1 al
 * 14» son 14 días, no 13: las vacaciones incluyen el día que se vuelve. */
export function diasCorridos(desde, hasta) {
  const a = fechaIngreso(desde);
  const b = fechaIngreso(hasta);
  if (!a || !b || b < a) return null;
  return Math.round((b - a) / (24 * 3600 * 1000)) + 1;
}

/* El estado de una persona en el año: corresponden, tomados (los tildados OK),
 * y pendientes. `tomadas` son las filas de `vacaciones_tomadas` de esa persona
 * en ese año; sólo las confirmadas descuentan —una vacación anotada pero sin
 * tildar todavía no se tomó—. Pendientes puede ser null si no se sabe cuántas
 * corresponden. */
export function estado(ingreso, tomadas, anio = anioVacaciones()) {
  const corresponden = diasPorAntiguedad(ingreso, anio);
  const tomados = (tomadas || [])
    .filter((t) => t && t.confirmado && !t.anulado_en)
    .reduce((s, t) => s + (Number(t.dias) || 0), 0);
  const pendientes = corresponden === null ? null : corresponden - tomados;
  return { corresponden, tomados, pendientes, proporcional: esProporcional(ingreso, anio) };
}
